import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';

const STORAGE_KEY = 'fff_cookie_ack_v1';

/**
 * Minimal ICO-compliant cookie notice — slim non-blocking bottom strip.
 * We only use strictly-necessary cookies so this is informational, not consent-gated.
 * Redesigned Feb 2026: the previous card-modal was consuming ~200px of the
 * mobile fold and overlapping the BottomNav. Now it's a low-profile 44px
 * dismissable strip above the BottomNav that never blocks primary content.
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      try {
        if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
      } catch { /* private mode / SSR safety */ }
    }, 900);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* noop */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie notice"
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-[68px] lg:bottom-3 z-[9500] mx-auto lg:mx-4 max-w-[860px] flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full border border-white/10 bg-[color:var(--fff-glass-bg)] backdrop-blur-xl shadow-lg"
      style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
    >
      <span aria-hidden="true" className="hidden sm:inline text-[10px] font-mono uppercase tracking-[0.16em] font-black text-[#BEFF38]">Cookies</span>
      <p className="flex-1 min-w-0 text-[11.5px] sm:text-xs text-zinc-300 leading-snug truncate">
        Strictly-necessary only. No ads, no cross-site tracking.{' '}
        <Link
          to="/privacy"
          data-testid="cookie-banner-privacy"
          onClick={dismiss}
          className="text-zinc-100 underline underline-offset-2 hover:text-white"
        >
          Privacy
        </Link>
      </p>
      <button
        onClick={dismiss}
        data-testid="cookie-banner-accept"
        className="shrink-0 inline-flex items-center gap-1 bg-[#BEFF38] hover:bg-[#a8e62e] text-black font-black uppercase tracking-wider text-[10px] px-3 py-1.5 rounded-full"
      >
        Got it
      </button>
      <button
        onClick={dismiss}
        aria-label="Dismiss cookie notice"
        data-testid="cookie-banner-close"
        className="shrink-0 p-1 rounded-full text-zinc-500 hover:text-white hover:bg-white/5"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
